/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import { untranslated } from "@i18n";
import { type ColumnData, type UseGanttViewsArgs } from "@WebTypes/scheduleDomain";
import { GANTT_GROUP_BY_RESOURCE } from "@Helpers/constants";
import type { GanttView, GanttViewData } from "@ServerTypes/layoutView/layoutView.schema";

export const PRESTIGE_PROD_HOST = "prestige.app.cube.ms";
export const isPrestigeProd = (): boolean =>
  process.env.REACT_APP_ENV === "prod" && globalThis.location.host === PRESTIGE_PROD_HOST;

const NON_TOGGLEABLE_COLUMN_NAMES = new Set(["add", "CustomIcon"]);
export const PRESTIGE_ONLY_COLUMNS = ["work%Complete", "%Complete"];
export const isPrestigeOnlyColumn = (name?: string | number): boolean =>
  PRESTIGE_ONLY_COLUMNS.includes(String(name ?? ""));
export const isToggleableGanttColumn = (name?: string | number): boolean => {
  const columnName = String(name ?? "");
  return Boolean(columnName) && !NON_TOGGLEABLE_COLUMN_NAMES.has(columnName);
};

export const useGanttViews = ({
  gantt,
  isGroupedRef,
  ganttFilterConditionsRef,
  applyGanttFilters,
  setSavedColumns,
  setIsGrouped,
  scheduleVersionId,
}: UseGanttViewsArgs) => {
  const [viewState, setViewState] = useState<{ currentView: GanttView | null; pendingViewData?: GanttViewData }>({
    currentView: null,
  });
  const [triggerUpdateCurrentView, setTriggerUpdateCurrentView] = useState<GanttViewData | null>(null);
  const { currentView, pendingViewData } = viewState;
  const isApplyingView = Boolean(pendingViewData);

  const applyGanttView = (viewData: GanttViewData) => {
    if (!viewData || !gantt.config?.columns) return;

    const nextConditions = viewData.filterConditions ? viewData.filterConditions : {};
    ganttFilterConditionsRef.current = nextConditions;

    const groupByResource = viewData.groupBy === GANTT_GROUP_BY_RESOURCE;

    gantt.batchUpdate(() => {
      applyGanttFilters(nextConditions);

      if (Array.isArray(viewData.visibleColumns)) {
        const visibleSet = new Set<string>(viewData.visibleColumns);
        const chooserSelection: ColumnData[] = [];
        const columns = gantt.config.columns;
        for (const column of columns) {
          const colName = column.name;
          if (!isToggleableGanttColumn(colName)) continue;
          if (isPrestigeOnlyColumn(colName) && !isPrestigeProd()) continue;
          const shouldShow = visibleSet.has(String(colName));
          column.hide = !shouldShow;
          if (shouldShow) {
            chooserSelection.push({ id: String(colName), label: column.label ?? String(colName), isChecked: true });
          }
        }
        setSavedColumns(chooserSelection);
      }

      gantt.config.readonly = groupByResource || Boolean(scheduleVersionId);
      setIsGrouped(groupByResource);

      if (groupByResource) {
        gantt.groupBy({
          groups: gantt.serverList("resourceOptions"),
          relation_property: "owner",
          group_id: "resource_id",
          group_text: "text",
          default_group_label: untranslated("Unassigned"),
          save_tree_structure: true,
        });
        isGroupedRef.current = true;
      } else if (isGroupedRef.current) {
        gantt.groupBy(false);
        isGroupedRef.current = false;
      }
    });
  };

  const createNewLayoutData = (): GanttViewData => {
    const visibleColumns: string[] = [];
    const columns = gantt.config.columns ?? [];
    columns.forEach((col) => {
      if (!isToggleableGanttColumn(col.name)) return;
      if (!col.hide && col.name) visibleColumns.push(String(col.name));
    });
    return {
      visibleColumns,
      groupBy: currentView?.data?.groupBy ?? null,
      filterConditions: ganttFilterConditionsRef.current ?? {},
    };
  };

  const notifyViewMutation = () => {
    setTriggerUpdateCurrentView(createNewLayoutData());
  };

  const selectView = (view: GanttView) => {
    setViewState({ currentView: view, pendingViewData: view.data });
  };

  const onClickLayout = (view: GanttView) => selectView(view);
  // ViewsNewFlow signals `false` when the user has no saved view — nothing to select then.
  const onViewsLoaded = (view: GanttView | false) => {
    if (view) selectView(view);
  };

  useEffect(() => {
    if (!pendingViewData) return;

    const applyViewTimeout = setTimeout(() => {
      applyGanttView(pendingViewData);
      setViewState((prevState) =>
        prevState.pendingViewData === pendingViewData ? { ...prevState, pendingViewData: undefined } : prevState,
      );
    }, 0);

    return () => clearTimeout(applyViewTimeout);
  }, [viewState]);

  return {
    currentView,
    isApplyingView,
    triggerUpdateCurrentView,
    onClickLayout,
    onViewsLoaded,
    createNewLayoutData,
    notifyViewMutation,
  };
};
